/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import { forwardRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Divider,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ExpandMore,
  Facebook,
  Instagram,
  Settings,
  GroupWork,
  Verified,
  InfoOutlined,
  CheckCircleOutline,
  ArrowForward,
} from "@mui/icons-material";
import ModalLayout from "src/components/shared/atoms/modalLayout";
import YouTubePreview from "src/components/shared/molecules/channelYoutubePreviewer";

const InstagramInstructionsModal = forwardRef<
  any,
  { setModalState?: (open: boolean) => void }
>(({ setModalState }, ref) => {
  const navigate = useNavigate();

  const StepItem = ({
    icon,
    title,
    description,
    caution,
  }: {
    icon: any;
    title: string;
    description: string;
    caution?: string;
  }) => (
    <Box sx={{ display: "flex", mb: 3, gap: 2 }}>
      <Avatar
        sx={{
          background:
            "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
          color: "#fff",
          width: 40,
          height: 40,
          boxShadow: "0 2px 8px rgba(188, 24, 136, 0.25)",
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 0.5, color: "#2d3436" }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.6 }}>
          {description}
        </Typography>
        {caution && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "#cc2366",
              fontStyle: "italic",
              fontWeight: 500,
            }}
          >
            {caution}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <ModalLayout
      headerText="Instagram Business Account Connection Flow"
      ref={ref}
      setModalState={setModalState}
      modalName={null}
      modalCustomStyle={{
        maxWidth: "600px",
        minHeight: "400px",
        width: "100%",
        borderRadius: "16px",
      }}
    >
      <Box
        sx={{
          maxHeight: "75vh",
          overflowY: "auto",
          p: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e0e0e0",
            borderRadius: "10px",
          },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            mb: 2,
            color: "#4d1277",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
        >
          Connect Instagram Business Account
        </Typography>

        {/* CRITICAL PREREQUISITE ALERT */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #faf5ff 0%, #fff 100%)",
            border: "1px solid #e9d5ff",
            borderRadius: "12px",
            p: 2.5,
            mb: 4,
            boxShadow: "0 4px 12px rgba(77, 18, 119, 0.05)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#4d1277",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <Verified sx={{ color: "#4d1277", fontSize: "1.5rem" }} />
            <Typography
              variant="subtitle2"
              sx={{ color: "#4d1277", fontWeight: 800, fontSize: "0.95rem" }}
            >
              REQUIRED FIRST STEP
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#2d3436", mb: 1 }}
          >
            Add your Facebook Page to KuikWit BEFORE connecting Instagram.
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#636e72", display: "block", mb: 2 }}
          >
            This ensures webhooks and messaging are correctly synchronized
            between both platforms.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              color: "#4d1277",
              borderColor: "#4d1277",
              fontSize: "0.8rem",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f3e8ff", borderColor: "#4d1277" },
            }}
            onClick={() => navigate("/settings/facebook")}
          >
            Go to Facebook Settings
          </Button>
        </Box>

        <YouTubePreview videoId="ELf2MlpUUNY" />

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, mb: 3, color: "#2d3436" }}
          >
            Connection Steps
          </Typography>

          <StepItem
            icon={<Facebook fontSize="small" />}
            title="1. Authenticate with Meta"
            description="Log in using the Facebook account that manages your target Instagram profile."
          />

          <StepItem
            icon={<Settings fontSize="small" />}
            title="2. Professional Account Check"
            description="Ensure your Instagram is a 'Professional' (Business or Creator) account. Personal accounts won't work."
            caution="Quick Tip: Instagram profiles should only be connected to ONE Facebook Page at a time."
          />

          <StepItem
            icon={<Instagram fontSize="small" />}
            title="3. Map to Facebook Page"
            description="Select the verified Facebook Page already added to KuikWit to link with your Instagram."
          />

          <StepItem
            icon={<CheckCircleOutline fontSize="small" />}
            title="4. Verification & Sync"
            description="Once authorized, your Instagram messages will start appearing in the KuikWit Inbox."
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Accordions for secondary info */}
        <Box sx={{ mb: 2 }}>
          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              mb: 1.5,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupWork fontSize="small" sx={{ color: "#4d1277" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Business Portfolio Rules
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                � <strong>Portfolio Mapping:</strong> Your Facebook Page and
                Instagram Account should ideally be in the same Meta Business
                Portfolio.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                � <strong>Full Control:</strong> Ensure your logged-in Facebook
                account has "Full Control" or "Everything" permissions for the
                linked Page.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                � <strong>Auto-Creation:</strong> If assets aren't in a
                portfolio, Meta may create a new one automatically during the
                connection flow.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlined fontSize="small" sx={{ color: "#4d1277" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Troubleshooting
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Account ID not returned"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Meta sometimes processes the connection in the background. If
                  the connection fails, please wait 30 seconds and retry the
                  flow.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Page already connected"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Verify your Instagram isn't already tied to a different
                  Facebook Page in your Instagram Mobile app settings.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mt: 2, fontSize: "0.8rem" }}>
                Need more help? Check the{" "}
                <a
                  href="https://kuikwit.com/instagram-integration"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#4d1277", fontWeight: 600 }}
                >
                  Full Integration Guide
                </a>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Video Guide Link */}
        <Box mt={4} mb={2} textAlign="center">
          <Typography
            variant="body2"
            mt={2}
            sx={{ fontWeight: 600, color: "#4d1277" }}
          >
            <a
              href="https://kuikwit.com/instagram-integration"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "underline",
              }}
            >
              View complete Instagram integration guide ?
            </a>
          </Typography>
        </Box>
      </Box>
    </ModalLayout>
  );
});

export default InstagramInstructionsModal;
